import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWebSocketContextSafe } from '../../../features/real-time-updates/useWebSocketContext';
import type { Incident, IncidentsStats } from '../types';
import { generateMockIncidents, calculateResolutionTime } from '../utils';
import { REFRESH_INTERVAL } from '../constants';

const QUERY_KEY = ['incidents'];

interface UseIncidentsResult {
    incidents: Incident[];
    stats: IncidentsStats;
    isLoading: boolean;
    isFetching: boolean;
    error: Error | null;
    refresh: () => Promise<void>;
    lastUpdate: string | null;
}

const fetchIncidents = async (): Promise<Incident[]> => {
    await new Promise(resolve => setTimeout(resolve, 800));
    return generateMockIncidents();
};

export const useIncidents = (): UseIncidentsResult => {
    const queryClient = useQueryClient();
    const wsContext = useWebSocketContextSafe();
    const lastMessageRef = useRef(wsContext?.lastMessage);
    
    const {
        data: incidents = [],
        isLoading,
        isFetching,
        error,
        refetch
    } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: fetchIncidents,
        refetchInterval: REFRESH_INTERVAL,
        staleTime: 15000,
        retry: 3,
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000)
    });

    useEffect(() => {
        if (!wsContext?.lastMessage || wsContext.lastMessage === lastMessageRef.current) {
            return;
        }

        lastMessageRef.current = wsContext.lastMessage;
        const { type, data } = wsContext.lastMessage;

        if (type === 'incident_created') {
            queryClient.setQueryData<Incident[]>(QUERY_KEY, old => {
                if (!old) return old;
                const newIncident = data as Incident;
                if (old.some(i => i.id === newIncident.id)) return old;
                return [newIncident, ...old];
            });
        } else if (type === 'incident_updated') {
            queryClient.setQueryData<Incident[]>(QUERY_KEY, old => {
                if (!old) return old;
                const updated = data as Incident;
                return old.map(i => i.id === updated.id ? { ...i, ...updated } : i);
            });
        } else if (type === 'incident_resolved') {
            queryClient.setQueryData<Incident[]>(QUERY_KEY, old => {
                if (!old) return old;
                const resolved = data as { id: string; resolvedAt: string };
                return old.map(i => 
                    i.id === resolved.id 
                        ? { ...i, status: 'resolved', resolvedAt: resolved.resolvedAt, updatedAt: resolved.resolvedAt }
                        : i
                );
            });
        }
    }, [wsContext?.lastMessage, queryClient]);

    const stats = useMemo<IncidentsStats>(() => {
        const total = incidents.length;
        const active = incidents.filter(i => i.status === 'active' || i.status === 'investigating').length;
        const critical = incidents.filter(i => i.type === 'critical').length;
        const warning = incidents.filter(i => i.type === 'warning').length;
        const resolved = incidents.filter(i => i.status === 'resolved').length;
        
        const resolvedIncidents = incidents.filter(i => i.resolvedAt);
        const avgResolutionTime = resolvedIncidents.length > 0
            ? resolvedIncidents.reduce((sum, i) => {
                const time = calculateResolutionTime(i);
                return sum + (time || 0);
                }, 0) / resolvedIncidents.length
            : 0;

        return {
            total,
            active,
            critical,
            warning,
            resolved,
            avgResolutionTime: Math.round(avgResolutionTime)
        };
    }, [incidents]);

    const refresh = useCallback(async () => {
        await refetch();
    }, [refetch]);

    return {
        incidents,
        stats,
        isLoading,
        isFetching,
        error: error as Error | null,
        refresh,
        lastUpdate: incidents[0]?.updatedAt || null
    };
};